import { Globals as AnimatedGlobals, useReducedMotion } from "@twilio-paste/animation-library";
import { StylingGlobals, ThemeProvider as StyledThemeProvider, CacheProvider } from "@twilio-paste/styling-library";
import { DarkTheme, DefaultTheme, StyledBase, pasteGlobalStyles } from "@twilio-paste/theme";
import createCache, { type Options } from "@emotion/cache";
import { CreateCustomTheme, CustomizationProviderProps } from "@twilio-paste/core/customization";
import merge from "deepmerge";
import * as React from "react";

// Modified version of https://github.com/twilio-labs/paste/tree/main/packages/paste-customization/src

const createCustomTheme = ({
    baseTheme,
    overrides,
    customBreakpoints,
    elements,
}: CreateCustomTheme) => {
    const breakpoints = customBreakpoints || baseTheme.breakpoints;
    const mergedTheme = merge(baseTheme, overrides);

    if (elements) {
        const uniqueKeys = new Set(Object.keys(elements).map((element) => element.toLowerCase()))
        const uniqueElements = Array.from(uniqueKeys)
        if (uniqueElements.length !== Object.keys(elements).length) {
            throw new Error(
                "[CustomizationProvider]: duplicate element names, even when using a different casing, is not supported with Paste.",
            );
        }
    }

    return {
        ...mergedTheme,
        breakpoints,
        elements,
    };
};



/**
 * The customization provider can be used to wrap a Paste applications and customize the
 * look and feel of the components you are using. You can provide overrides to the entire
 * theme, supply custom breakpoints for responsive design, and customize tagged DOM
 * elements within each paste component with custom CSS.
 *
 * @param {*} {
 *   baseTheme = 'default',
 *   customBreakpoints,
 *   elements,
 *   theme,
 *   disableAnimations = false,
 *   ...props
 * }
 * @return {*}
 */
export const CachedThemeProvider: React.FC<React.PropsWithChildren<CustomizationProviderProps & { cacheProps: Options }>> = ({
    baseTheme = "default",
    customBreakpoints,
    elements,
    theme,
    disableAnimations = false,
    cacheProps,
    ...props
}) => {
    const cache = React.useMemo(() => createCache(cacheProps), [cacheProps]);
    const prefersReducedMotion = useReducedMotion();
    React.useMemo(() => {
        AnimatedGlobals.assign({
            skipAnimation: disableAnimations || prefersReducedMotion,
        });
    }, [disableAnimations, prefersReducedMotion]);

    const customTheme = React.useMemo(
        () =>
            createCustomTheme({
                baseTheme: baseTheme === "dark" ? DarkTheme : DefaultTheme,
                overrides: theme || {},
                elements: elements || {},
                customBreakpoints,
            }),
        [baseTheme, customBreakpoints, elements, theme],
    );
 
    return (
        <CacheProvider value={cache}>
            <StyledThemeProvider theme={customTheme}>
                <StylingGlobals styles={pasteGlobalStyles({ theme: customTheme })} />
               { /* @ts-expect-error not sure why the linter is complaining here */}
                <StyledBase {...props} />
            </StyledThemeProvider>
        </CacheProvider>
    );
};

CachedThemeProvider.displayName = "PasteCachedCustomizationProvider";